import React, { useEffect, useMemo, useState } from "react";
import BikeCard from "./BikeCard";
import { apiGet } from "../../lib/apiClient";
// import BikeTimeline from "./BikeTimeline";

const ERAS = [
  {
    id: "pioneer",
    label: "Pioneer Era",
    years: "1912–1928",
    intro:
      "Early Matchless V-twins and touring machines that established the marque before the interwar years.",
    start: 1912,
    end: 1928,
  },
  {
    id: "interwar",
    label: "Interwar Innovation",
    years: "1929–1939",
    intro:
      "A period of ambitious engineering, sporting singles, unusual twins and bold pre-war experimentation.",
    start: 1929,
    end: 1938,
  },
  {
    id: "wartime",
    label: "Wartime Machines",
    years: "1939–1945",
    intro:
      "Military production and rugged utility motorcycles built for service in wartime conditions.",
    start: 1939,
    end: 1944,
  },
  {
    id: "postwar",
    label: "Post-War Utility",
    years: "1945–1954",
    intro:
      "Proven wartime engineering adapted for civilian use, with reliable singles for a rebuilding world.",
    start: 1945,
    end: 1954,
  },
  {
    id: "golden",
    label: "Golden Era & Competition",
    years: "1955–1966",
    intro:
      "The peak years of Matchless road, racing and scrambles development, from twins to legendary competition singles.",
    start: 1955,
    end: 1966,
  },
  {
    id: "hybrid",
    label: "Hybrid & Final Years",
    years: "1967–1968",
    intro:
      "The final AMC-flavoured off-road hybrids, where Matchless chassis thinking met Norton power.",
    start: 1967,
    end: 1968,
  },
  {
    id: "revival",
    label: "Revival Era",
    years: "1987–1990s",
    intro:
      "Later continuation models that kept the Matchless name alive long after the original factory era.",
    start: 1987,
    end: 1999,
  },
  {
    id: "modern",
    label: "Modern Heritage",
    years: "2014–2017",
    intro:
      "Boutique revival machines that reinterpreted the Matchless name for a modern audience.",
    start: 2014,
    end: 2017,
  },
];

function getStartYear(yearValue) {
  if (!yearValue) return null;

  const match = String(yearValue).match(/^(\d{4})-/);
  return match ? Number(match[1]) : null;
}

function getEndYear(yearValue) {
  if (!yearValue) return null;

  const match = String(yearValue).match(/-(\d{4})$/);
  return match ? Number(match[1]) : null;
}

function getEraForBike(bike) {
  const startYear = getStartYear(bike.year);

  if (!startYear) return "unknown";

  const era = ERAS.find(
    (item) => startYear >= item.start && startYear <= item.end
  );

  return era?.id || "unknown";
}

function sortBikesByYear(a, b) {
  const aStart = getStartYear(a.year) ?? 9999;
  const bStart = getStartYear(b.year) ?? 9999;

  if (aStart !== bStart) {
    return aStart - bStart;
  }

  const aEnd = getEndYear(a.year) ?? 9999;
  const bEnd = getEndYear(b.year) ?? 9999;

  if (aEnd !== bEnd) {
    return aEnd - bEnd;
  }

  return String(a.name || "").localeCompare(String(b.name || ""));
}

function normaliseBike(row) {
  return {
    id: row.id,
    name: row.name,
    image: row.image,
    description: row.description,
    year: row.year,
    engine: row.engine,
    horsepower: row.horsepower,
    topSpeed: row.topspeed,
    weight: row.weight,
    gearbox: row.gearbox,
    boreStroke: row.bore_stroke,
    compression: row.compression,
    carburettor: row.carburettor,
    ignition: row.ignition,
    estimatedProduction: row.estimated_production,
    estimatedSurvivors: row.estimated_survivors,
    estimateConfidence: row.estimate_confidence,
    wd: Boolean(row.wd),
    hasManual: Boolean(row.has_manual),
    slug: row.slug,
  };
}

const BikesTab = () => {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadBikes() {
      setLoading(true);
      setLoadError("");

      try {
        const { data } = await apiGet("/.netlify/functions/bikes-list");

        if (cancelled) return;

        const normalised = (data || []).map(normaliseBike).sort(sortBikesByYear);
        setBikes(normalised);
      } catch (err) {
        console.error("Failed to load bikes:", err);

        if (!cancelled) {
          setLoadError(err?.message || "Failed to load bikes");
          setBikes([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadBikes();

    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    return ERAS.map((era) => ({
      ...era,
      bikes: bikes.filter((bike) => getEraForBike(bike) === era.id),
    })).filter((era) => era.bikes.length > 0);
  }, [bikes]);

  const unknownBikes = useMemo(() => {
    return bikes.filter((bike) => getEraForBike(bike) === "unknown");
  }, [bikes]);

  if (loading) {
    return (
      <div className="tabcontent">
        <div className="jp-admin-panel">Loading bikes…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="tabcontent">
        <div className="jp-admin-panel">Failed to load bikes: {loadError}</div>
      </div>
    );
  }

  return (
    <div className="tabcontent">
      {/* <BikeTimeline bikes={bikes} /> */}

      {grouped.map((era, index) => (
        <section key={era.id} className={`bike-era bike-era--${era.id}`}>
          <div className="bike-era__header">
            <div className="bike-era__eyebrow">Era {index + 1}</div>

            <div className="bike-era__topline">
              <h2>{era.label}</h2>
              <span>{era.years}</span>
            </div>

            <p>{era.intro}</p>
          </div>

          <section className="bike-grid">
            {era.bikes.map((bike) => (
              <BikeCard
                key={bike.id || bike.slug || bike.name}
                bike={bike}
              />
            ))}
          </section>
        </section>
      ))}

      {unknownBikes.length > 0 && (
        <section className="bike-era bike-era--unknown">
          <div className="bike-era__header">
            <div className="bike-era__eyebrow">Unsorted</div>

            <div className="bike-era__topline">
              <h2>Other Machines</h2>
              <span>Unknown period</span>
            </div>

            <p>Machines that could not be placed automatically into a historical era.</p>
          </div>

          <section className="bike-grid">
            {unknownBikes.map((bike) => (
              <BikeCard
                key={bike.id || bike.slug || bike.name}
                bike={bike}
              />
            ))}
          </section>
        </section>
      )}
    </div>
  );
};

export default BikesTab;
